import React, { useState, useEffect } from 'react';
import { SectionHeader } from '@grnhse/seedling/lib/birch/typography';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import { Tooltip as BirchTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { PrimaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import styled  from 'styled-components';
import { standardRequest } from 'shared/utils/request';
import { withLocalePrefix } from 'shared/utils/translation';
import { SuccessFlash, ErrorFlash } from '@grnhse/seedling/lib/birch/components/flash';

import CandidateResponseLayout from './layout';
import SimpleMessage from './simple_message';

const t = withLocalePrefix('gdpr.manage_my_consent');

type TooltipProps = {
  label: string;
  show: boolean;
  children: React.ReactElement;
};

const Tooltip = (props: TooltipProps) => {
  if (props.show) {
    return <BirchTooltip label={props.label} ariaLabel={props.label}>{props.children}</BirchTooltip>;
  }
  else {
    return props.children;
  }
};

type Props = {
  companyName: string;
  token: string;
};

const ManageMyConsent = (props: Props) => {
  const [consentToProcessing, setConsentToProcessing] = useState(false);
  const [consentToRetention, setConsentToRetention] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const submit = () => {
    setSubmitting(true);
    setError(false);

    standardRequest.post('/gdpr/explicit_consent/consent_decisions', {
      token: props.token,
      processing_decision: consentToProcessing,
      retention_decision: consentToRetention
    })
      .then(() => setSubmitted(true))
      .catch(() => setError(true))
      .finally(() => setSubmitting(false));
  };

  useEffect(() => {
    if (!consentToProcessing) setConsentToRetention(false);
  }, [consentToProcessing]);

  const Form = () => (
    <Stack spacing={3}>
      <SectionHeader>
        {t('title', { company: props.companyName })}
      </SectionHeader>
      <Body>
        {t('body.summary', { company: props.companyName })}
        <Ul>
          <li>{t('body.processing_description')}</li>
          <li>{t('body.retention_description')}</li>
        </Ul>
      </Body>
      <Stack spacing={2}>
        <Checkbox
          label={t('checkboxes.processing', { company: props.companyName })}
          checked={consentToProcessing}
          onChange={setConsentToProcessing}
          value="processing"
          data-provides="processing-field"
        />
        <Tooltip
          label={t('checkboxes.tooltip')}
          show={!consentToProcessing}
        >
          <Checkbox
            disabled={!consentToProcessing}
            label={t('checkboxes.retention', { company: props.companyName })}
            checked={consentToRetention}
            onChange={setConsentToRetention}
            value="retention"
            data-provides="retention-field"
          />
        </Tooltip>
      </Stack>
      <PrimaryButton onClick={submit} disabled={submitting}>
        {t('submit_response')}
      </PrimaryButton>
    </Stack>
  );

  const ThankYou = () => (
    <SimpleMessage title={t('thank_you.title')} body={t('thank_you.body', { company: props.companyName })} />
  );

  return (
    <CandidateResponseLayout>
      <FlashWrapper>
        {submitted && <SuccessFlash message={t('response_submitted_successfully')} />}
        {error && <ErrorFlash message={t('something_went_wrong')} />}
      </FlashWrapper>
      {submitted ? <ThankYou /> : <Form />}
    </CandidateResponseLayout>
  );
};

const FlashWrapper = styled.div`
  position: fixed;
  top: 46px;
  left: 0;
  width: 100%;
`;

const Ul = styled.ul`
  margin: 0.5em 0 0 0;
  padding-inline-start: 1.5em;
`;

export default ManageMyConsent;
